import Vue from 'vue'
import { Component, Watch } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'
import { Getter } from 'vuex-class'

import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Account } from '@/modules/Account/interfaces/account'
import { showErrors, showNotification } from '@/utils/snack'
import { dateTimeToDisplay, dateToNow } from '@/utils/dateTime'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTabs from '@/components/AppTabs.vue'
import { RecordType } from '@/modules/RecordType/interfaces/record-type'
import ContactDialog from '@/modules/Account/scripts/accounts-list'
import RecordTypesHandler from '@/mixins/record-types'
import AccountDialog from '@/modules/Account/scripts/account-dialog'
import AccountDetailDetail from '@/modules/Account/views/detail.vue'
import AccountDetailContact from '@/modules/Account/views/contact.vue'
import { getAccount, editAccount } from '@/modules/Account/gql/account.gql'
import { formatPhoneNumber } from '@/utils/phoneNumberFormat'
import Source from '@/modules/Account/views/source.vue'
import Additional from '@/modules/Account/views/additional.vue'
import AccountDetailSelector from '../components/AccountDetailSelector.vue'
import { accountLevelValues, accountLevel, accountLevelKeys } from '../helper/account-level'
import { SnackType } from '@/interfaces/snack'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTabs,
    AccountDetailDetail,
    AccountDetailContact,
    Source,
    Additional,
    AccountDetailSelector
  }
})
export default class AccountDetail extends mixins(AccountDialog, ContactDialog, RecordTypesHandler) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Client Information'
    },
    {
      text: 'Accounts',
      to: { name: 'AccountsList' }
    }
  ]
  accountLevelKeys = accountLevelKeys
  accountLevel = accountLevel
  accountLevelValues = accountLevelValues
  accountName = ''
  formatPhoneNumber = formatPhoneNumber

  account: Account = {} as Account

  windowSize = {
    w: 0,
    h: 0,
    cW: 0
  }

  tabActive = 0
  tabItems = [
    { title: 'Details', route: 'AccountDetailDetail' },
    { title: 'Contacts', route: 'AccountDetailContact', isShowRightBorder: true },
    { title: 'Cases', route: 'AccountDetailCase' },
    { title: 'Quotes', route: 'AccountDetailQuotes' },
    { title: 'Policies', route: 'AccountDetailPolicy' },
    // { title: 'Source', route: 'AccountDetailSource' },
    { title: 'COI', route: 'AccountDetailCertificate' },
    { title: 'Claim', route: 'AccountDetailClaim', isShowRightBorder: true },
    { title: 'Assets', route: 'AccountDetailAssets' },
    { title: 'Certificates', route: 'AccountDetailCertificates', isShowRightBorder: true },
    { title: 'Attachments', route: 'AccountDetailDocument' }
    // { title: 'Additional', route: 'AccountDetailAdditional' }
  ]

  dateTimeToDisplay = dateTimeToDisplay
  dateToNow = dateToNow

  @Getter('recordType/accountRecordTypes') acctRTypes!: RecordType[]

  get tabTitles () {
    return this.tabItems.map(item => item.title)
  }

  get tabAdditions () {
    return this.tabItems.map(item => {
      if (item.title === 'Cases') {
        return this.account ? this.account.caseCount : ''
      } else {
        return ''
      }
    })
  }
  get tabAddBorder () {
    return this.tabItems.map(item => {
      if (item.isShowRightBorder) {
        return true
      } else {
        return false
      }
    })
  }

  get recordType () {
    if (this.$R.isEmpty(this.acctRTypes) || !this.account) {
      return null
    }
    const recordTypeId = this.account.recordType.id
    return this.$R.find((recordType: RecordType) => {
      return recordType.id === recordTypeId
    })(this.acctRTypes)
  }

  async mounted () {
    const { name } = this.$route
    const index = this.$R.findIndex((item: any) => item.route === name)(this.tabItems)
    this.tabActive = index
    this.onResize()
    this.getAccount()
  }

  @Watch('$route.params.id')
  onRouteParamUpdate (val: string) {
    this.breadcrumbs = [
      {
        text: 'Client Information'
      },
      {
        text: 'Accounts',
        to: { name: 'AccountsList' }
      }
    ]
    this.getAccount()
  }

  onResize () {
    const container = this.$refs.container as Vue & { clientWidth: number }
    this.windowSize = { w: window.innerWidth, h: window.innerHeight, cW: container.clientWidth }
  }

  clickTabHandler (index: number) {
    const item = this.tabItems[index]
    if (item.route && item.route !== this.$route.name) {
      this.$router.push({
        name: item.route
      })
    }
  }

  async getAccount () {
    const accountId = Number(this.$route.params.id)
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getAccount,
        variables: {
          accountId
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.account = result.data.getAccount as Account
      if (this.breadcrumbs.length === 2) {
        this.breadcrumbs.push({
          text: this.account.name
        })
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  handleGetAccount () {
    this.getAccount()
    // this.recordType()
  }

  async deleteAcctConfirm () {
    await this.deleteAcctPost()
    this.$router.replace({
      name: 'AccountsList'
    })
  }

  routeToContactDetail () {
    if (this.account && this.account.primaryContact) {
      this.$router.push({
        name: 'ContactDetailDetail',
        params: {
          id: this.account.primaryContact.id + ''
        }
      })
    }
  }

  editLevel (index: number) {
    const accountInput = {
      level: +accountLevelKeys[index] || null
    }
    this.editAccountRequest(accountInput)
  }

  async editAccountRequest (accountInput: any) {
    const { account } = this
    try {
      const result = await this.$apollo.mutate({
        mutation: editAccount,
        variables: {
          accountId: +this.account.id,
          accountInput: {
            name: account.name || '',
            recordTypeId: +account.recordType.id || null,
            ...accountInput
          }
        }
      })

      const res = result.data.editAccount
      if (res) {
        showNotification({
          msg: 'Update account successfully',
          type: SnackType.Success
        })
        this.getAccount()
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }
}
